import { useForm, usePage } from '@inertiajs/react'
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  Field,
  Label,
  Input,
  Select,
  Button,
  ErrorMessage,
  Text,
} from '@/components/ui'
import { AssignmentList } from '@/components/common/AssignmentList'
import { useTranslations, LANGUAGE_NAMES } from '@/lib/i18n'
import type { SharedProps } from '@/types'

/**
 * "Invite user" create dialog, opened from the Users index. Submits a POST; on
 * validation error the controller redirects back with Inertia errors, keeping
 * the dialog open with the field messages.
 */
export function InviteUserModal({
  open,
  onClose,
  roles,
  assignableMerchants,
}: {
  open: boolean
  onClose: () => void
  roles: string[]
  assignableMerchants: { id: number; name: string }[]
}) {
  const { t } = useTranslations()
  const { props } = usePage<SharedProps>()
  const form = useForm<{ name: string; email: string; role: string; locale: string; merchant_ids: number[] }>({
    name: '',
    email: '',
    role: roles[roles.length - 1] ?? 'viewer',
    locale: props.locale,
    merchant_ids: [],
  })

  // operators/viewers are scoped to assigned merchants; admins have all access.
  const showMerchants = form.data.role === 'operator' || form.data.role === 'viewer'

  function close() {
    form.reset()
    form.clearErrors()
    onClose()
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ user: data }))
    form.post('/users', { preserveScroll: true, onSuccess: close })
  }

  function toggleMerchant(id: number, checked: boolean) {
    form.setData('merchant_ids', checked ? [...form.data.merchant_ids, id] : form.data.merchant_ids.filter((x) => x !== id))
  }

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>{t('users.new_title')}</DialogTitle>
      <form onSubmit={submit}>
        <DialogBody className="flex flex-col gap-5">
          <Field>
            <Label>{t('users.name')}</Label>
            <Input value={form.data.name} onChange={(e) => form.setData('name', e.target.value)} autoFocus />
            {form.errors.name && <ErrorMessage>{form.errors.name}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('users.email')}</Label>
            <Input type="email" value={form.data.email} onChange={(e) => form.setData('email', e.target.value)} />
            {form.errors.email && <ErrorMessage>{form.errors.email}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('users.role')}</Label>
            <Select value={form.data.role} onChange={(e) => form.setData('role', e.target.value)}>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {t(`roles.${role}`)}
                </option>
              ))}
            </Select>
            {form.errors.role && <ErrorMessage>{form.errors.role}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('users.language')}</Label>
            <Select value={form.data.locale} onChange={(e) => form.setData('locale', e.target.value)}>
              {props.available_locales.map((code) => (
                <option key={code} value={code}>
                  {LANGUAGE_NAMES[code] ?? code}
                </option>
              ))}
            </Select>
            <Text className="text-text/60 text-sm">{t('users.language_hint')}</Text>
          </Field>

          {showMerchants && (
            <Field>
              <Label>{t('users.assigned_merchants')}</Label>
              {assignableMerchants.length === 0 ? (
                <Text className="text-text/60">{t('merchants.empty')}</Text>
              ) : (
                <AssignmentList
                  items={assignableMerchants.map((m) => ({ id: m.id, label: m.name }))}
                  selected={form.data.merchant_ids}
                  onToggle={toggleMerchant}
                  searchPlaceholder={t('common.search')}
                />
              )}
            </Field>
          )}
        </DialogBody>

        <DialogActions>
          <Button plain type="button" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" color="brand" disabled={form.processing}>
            {t('users.invite')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
